import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEventTracking } from '@services/statsig';

import {
  useCreateSavedReportView,
  useLazyExportReportCsv,
  useLazyExportReportPdf,
  useLazyExportReportXlsx,
  useLazyRunReport,
  useReportDefinitions,
  useSavedReportViews,
} from '../store/hooks';
import type { ReportDefinition, ReportFilterSpec, SavedViewParams } from '../types';
import { defaultWindow, resolveWindow, windowForDefinition } from '../utils/format';

interface UseReportRunArgs {
  reportKey: string;
}

const joinOrUndefined = (values: string[]): string | undefined =>
  values.length > 0 ? values.join(',') : undefined;

// Working state (basis/group/columns) auto-persists per report so an unsaved
// configuration survives navigation. The date window deliberately does not —
// a stale window restoring silently is worse than the default; saved views
// own durable windows via their relative/absolute spec.
//
// Multi-tab safety: localStorage is WRITTEN only from explicit user actions
// (the token setters), never from effects — a dormant two-week-old tab can
// re-render all it likes and will never clobber newer state from another
// tab. If a human actually clicks in an old tab, that IS the latest intent
// (last-human-action-wins). Reads happen once at mount and entries older
// than the TTL are ignored, so ancient state never resurrects. Live cross-tab
// mirroring (the redux-state-sync BroadcastChannel pattern) is deliberately
// NOT used: an open report should not reshuffle because another tab clicked.
const WORKING_STATE_PREFIX = 'reports.workingState.';
// Excel export options (tab dimension, column scope) persist per report.
const XLSX_PREFS_PREFIX = 'reports.xlsxPrefs.';
// PDF document options (page split, column scope) persist per report.
const PDF_PREFS_PREFIX = 'reports.pdfPrefs.';

const WORKING_STATE_VERSION = 1;
const WORKING_STATE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface WorkingState {
  v?: number;
  updated_at?: number;
  basis?: string;
  group_by?: string[];
  columns?: string[];
  sort_key?: string;
  sort_dir?: string;
  filters?: ReportFilterSpec[];
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

export interface XlsxPrefs {
  /** Dimension key to split tabs by; '' = single sheet. */
  sheetBy: string;
  /** true = all detail columns; false = the picked view columns. */
  allColumns: boolean;
}

export interface PdfPrefs {
  /** Dimension key to start a new page per value; '' = continuous. */
  pageBy: string;
  /** true = all detail columns; false = the picked view columns. */
  allColumns: boolean;
}

const readJson = (key: string): unknown => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeJson = (key: string, value: unknown): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full/blocked — working state is a convenience, never fatal.
  }
};

const sameList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/**
 * Business logic for the console run page. Configure-after-entry: the report
 * auto-runs on open with defaults (or the saved view's params) and re-runs
 * on every token change — there is no Run button.
 */
export const useReportRun = ({ reportKey }: UseReportRunArgs) => {
  const { reports } = useEventTracking();
  const [searchParams] = useSearchParams();
  const savedViewId = searchParams.get('view');

  const { data: definitionsData, isLoading: definitionsLoading } = useReportDefinitions();
  const definition: ReportDefinition | undefined = useMemo(
    () => definitionsData?.reports.find(report => report.key === reportKey),
    [definitionsData, reportKey],
  );

  const { data: savedViewsData } = useSavedReportViews({ reportKey });
  const savedView = useMemo(
    () =>
      savedViewId
        ? savedViewsData?.views.find(view => view.report_saved_view_id === savedViewId)
        : undefined,
    [savedViewId, savedViewsData],
  );

  const workingState = useMemo(() => {
    if (savedViewId) return null; // a saved view in the URL outranks stored state
    const stored = readJson(`${WORKING_STATE_PREFIX}${reportKey}`) as WorkingState | null;
    if (!stored || stored.v !== WORKING_STATE_VERSION) return null;
    if (!stored.updated_at || Date.now() - stored.updated_at > WORKING_STATE_TTL_MS)
      return null;
    return stored;
  }, [reportKey, savedViewId]);

  // Recents/palette deep-link with explicit params (?start&end&basis&group).
  const [window_, setWindow] = useState(() => {
    const start = searchParams.get('start');
    const end = searchParams.get('end');
    return start && end ? { start, end } : defaultWindow();
  });
  // The definition owns the opening window (e.g. the schedule opens on
  // today). Until it loads — or a URL/saved-view window wins — runs hold.
  const [windowReady, setWindowReady] = useState(
    () => Boolean(searchParams.get('start') && searchParams.get('end')),
  );
  useEffect(() => {
    if (!definition || windowReady || savedViewId) return;
    setWindow(
      windowForDefinition(
        definition.default_window_days ?? 14,
        Boolean(definition.forward_window),
      ),
    );
    setWindowReady(true);
  }, [definition, windowReady, savedViewId]);
  const setWindowAndMark = useCallback((next: { start: string; end: string }) => {
    setWindowReady(true);
    setWindow(next);
  }, []);
  const [basis, setBasis] = useState<string | undefined>(
    () => searchParams.get('basis') ?? workingState?.basis ?? undefined,
  );
  // null = "not chosen yet" → defaults to the report's first dimension.
  const [groupBy, setGroupByState] = useState<string[] | null>(() => {
    const group = searchParams.get('group');
    if (group !== null) return group ? group.split(',') : [];
    return workingState?.group_by ?? null;
  });
  // null = "not chosen yet" → the definition's default_columns.
  const [columns, setColumnsState] = useState<string[] | null>(
    () => workingState?.columns ?? null,
  );
  // Active row filters; [] = none.
  const [filters, setFiltersState] = useState<ReportFilterSpec[]>(
    () => workingState?.filters ?? [],
  );
  // null = no row sort → rows keep dataset order; bands keep the sort measure.
  const [sort, setSortState] = useState<SortState | null>(() =>
    workingState?.sort_key
      ? {
          key: workingState.sort_key,
          dir: workingState.sort_dir === 'desc' ? 'desc' : 'asc',
        }
      : null,
  );
  const [appliedSavedViewId, setAppliedSavedViewId] = useState<string | null>(null);
  // Snapshot of the last in-session save — clears the dirty flag without a reload.
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState<string | null>(null);

  const [runReport, runState] = useLazyRunReport();
  const [exportCsv, exportState] = useLazyExportReportCsv();
  const [exportXlsx, exportXlsxState] = useLazyExportReportXlsx();
  const [exportPdf, exportPdfState] = useLazyExportReportPdf();
  const [createSavedView, createViewState] = useCreateSavedReportView();

  // A report only understands its own bases/dimensions/columns. Token state
  // can arrive from foreign or stale sources — another report's tokens, an
  // old localStorage entry, a key the backend has since renamed — so every
  // effective value is validated against the definition instead of being
  // sent to the API verbatim.
  const effectiveBasis = useMemo(() => {
    if (!definition) return basis;
    return basis && definition.supported_bases.includes(basis)
      ? basis
      : definition.supported_bases[0];
  }, [basis, definition]);
  const effectiveGroupBy = useMemo(() => {
    const fallback = definition?.dimensions[0] ? [definition.dimensions[0].key] : [];
    if (groupBy === null) return fallback;
    if (!definition) return groupBy;
    const valid = groupBy.filter(key =>
      definition.dimensions.some(dimension => dimension.key === key),
    );
    // Empty-by-choice stays ungrouped; empty-by-filtering means foreign state.
    return groupBy.length > 0 && valid.length === 0 ? fallback : valid;
  }, [groupBy, definition]);
  const effectiveColumns = useMemo(() => {
    const defaults = definition?.default_columns ?? [];
    if (columns === null) return defaults;
    if (!definition) return columns;
    const valid = columns.filter(key =>
      definition.detail_columns.some(column => column.key === key),
    );
    return valid.length > 0 ? valid : defaults;
  }, [columns, definition]);
  // Only complete filters on currently-filterable columns reach the API; a
  // half-built filter row in the popover never poisons a run.
  const valuelessOps = ['is_empty', 'not_empty'];
  const effectiveFilters = useMemo(() => {
    if (!definition) return [];
    return filters.filter(item => {
      const column = definition.detail_columns.find(c => c.key === item.field);
      if (!column?.filterable) return false;
      return valuelessOps.includes(item.op) || (item.values?.length ?? 0) > 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, definition]);

  const effectiveSort = useMemo(() => {
    if (!sort) return null;
    if (
      definition &&
      !definition.detail_columns.some(column => column.key === sort.key)
    )
      return null;
    return sort;
  }, [sort, definition]);
  const isDefaultColumns = useMemo(
    () =>
      columns === null ||
      (definition != null && sameList(columns, definition.default_columns)),
    [columns, definition],
  );

  // User-action-only persistence: called from the setters below, never from
  // an effect (see the multi-tab note on WORKING_STATE_PREFIX).
  const persistWorkingState = useCallback(
    (next: {
      basis?: string;
      group_by?: string[];
      columns?: string[] | null;
      sort?: SortState | null;
      filters?: ReportFilterSpec[];
    }) => {
      const nextSort = next.sort === undefined ? sort : next.sort;
      writeJson(`${WORKING_STATE_PREFIX}${reportKey}`, {
        v: WORKING_STATE_VERSION,
        updated_at: Date.now(),
        basis: next.basis ?? effectiveBasis,
        group_by: next.group_by ?? effectiveGroupBy,
        columns: next.columns === undefined ? columns ?? undefined : next.columns ?? undefined,
        sort_key: nextSort?.key,
        sort_dir: nextSort?.dir,
        filters: next.filters ?? filters,
      } satisfies WorkingState);
    },
    [reportKey, effectiveBasis, effectiveGroupBy, columns, sort, filters],
  );

  const setBasisAndPersist = useCallback(
    (next: string) => {
      setBasis(next);
      persistWorkingState({ basis: next });
    },
    [persistWorkingState],
  );
  const setGroupBy = useCallback(
    (next: string[]) => {
      setGroupByState(next);
      persistWorkingState({ group_by: next });
    },
    [persistWorkingState],
  );
  const setColumns = useCallback(
    (next: string[] | null) => {
      setColumnsState(next);
      persistWorkingState({ columns: next });
    },
    [persistWorkingState],
  );
  const setSort = useCallback(
    (next: SortState | null) => {
      setSortState(next);
      persistWorkingState({ sort: next });
    },
    [persistWorkingState],
  );
  const setFilters = useCallback(
    (next: ReportFilterSpec[]) => {
      setFiltersState(next);
      persistWorkingState({ filters: next });
    },
    [persistWorkingState],
  );

  // Deltas ride along whenever the definition declares delta-wanting stats.
  const compare = useMemo(
    () =>
      definition?.stat_cards.some(card => card.wants_delta)
        ? 'prior_period'
        : undefined,
    [definition],
  );

  // Apply a saved view once: resolve its window and set tokens. The auto-run
  // effect below picks the change up.
  useEffect(() => {
    if (!definition) return;
    if (savedViewId && savedView && appliedSavedViewId !== savedViewId) {
      const params = (savedView.params || {}) as SavedViewParams;
      const resolved = resolveWindow(params.window);
      setWindow(resolved);
      setWindowReady(true);
      setBasis(params.basis ?? definition.supported_bases[0]);
      setGroupByState(params.group_by ?? []);
      setColumnsState(params.columns ?? null);
      setFiltersState(params.filters ?? []);
      setSortState(
        params.sort_key
          ? { key: params.sort_key, dir: params.sort_dir === 'desc' ? 'desc' : 'asc' }
          : null,
      );
      setAppliedSavedViewId(savedViewId);
      reports.savedViewOpened({ report_key: reportKey });
    }
  }, [definition, savedViewId, savedView, appliedSavedViewId, reportKey, reports]);

  // Dirty = current slice differs from its baseline (the applied saved view,
  // the last in-session save, or the report defaults). The window is
  // excluded: browsing dates is exploration, not configuration.
  const snapshotOf = useCallback(
    (
      snapBasis: string | undefined,
      snapGroup: string[],
      snapColumns: string[],
      snapSort: SortState | null,
      snapFilters: ReportFilterSpec[] = [],
    ) =>
      JSON.stringify({
        b: snapBasis,
        g: snapGroup,
        c: snapColumns,
        s: snapSort,
        f: snapFilters,
      }),
    [],
  );
  const currentSnapshot = snapshotOf(
    effectiveBasis,
    effectiveGroupBy,
    effectiveColumns,
    effectiveSort,
    effectiveFilters,
  );
  const baselineSnapshot = useMemo(() => {
    if (!definition) return currentSnapshot;
    if (lastSavedSnapshot) return lastSavedSnapshot;
    if (savedViewId && savedView) {
      const params = (savedView.params || {}) as SavedViewParams;
      return snapshotOf(
        params.basis ?? definition.supported_bases[0],
        params.group_by ?? [],
        params.columns ?? definition.default_columns,
        params.sort_key
          ? { key: params.sort_key, dir: params.sort_dir === 'desc' ? 'desc' : 'asc' }
          : null,
        params.filters ?? [],
      );
    }
    return snapshotOf(
      definition.supported_bases[0],
      definition.dimensions[0] ? [definition.dimensions[0].key] : [],
      definition.default_columns,
      null,
    );
  }, [definition, lastSavedSnapshot, savedViewId, savedView, snapshotOf, currentSnapshot]);
  const isDirty = currentSnapshot !== baselineSnapshot;

  // Auto-run: on open and on any token change.
  useEffect(() => {
    if (!definition || !windowReady) return;
    // With a saved view in the URL, wait until its params are applied.
    if (savedViewId && appliedSavedViewId !== savedViewId) return;
    reports.runSubmitted({
      report_key: reportKey,
      basis: effectiveBasis,
      group_by: effectiveGroupBy.join(','),
    });
    runReport({
      reportKey,
      startDate: window_.start,
      endDate: window_.end,
      basis: effectiveBasis,
      groupBy: joinOrUndefined(effectiveGroupBy),
      columns: joinOrUndefined(effectiveColumns),
      compare,
      sort: effectiveSort?.key,
      sortDir: effectiveSort?.dir,
      filters: effectiveFilters.length ? JSON.stringify(effectiveFilters) : undefined,
    });
    // reports is memoized by useEventTracking; runReport is a stable RTK
    // trigger — the suppression exists only because the lint rule can't see
    // that and would demand both.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    definition,
    windowReady,
    savedViewId,
    appliedSavedViewId,
    reportKey,
    window_.start,
    window_.end,
    effectiveBasis,
    effectiveGroupBy,
    effectiveColumns,
    effectiveSort,
    effectiveFilters,
    compare,
    reports,
  ]);

  const triggerDownload = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  const downloadCsv = useCallback(
    async (options?: { allColumns?: boolean }) => {
      const allColumns = options?.allColumns ?? false;
      reports.exportClicked({
        report_key: reportKey,
        basis: effectiveBasis,
        grouped: effectiveGroupBy.length > 0,
        format: 'csv',
        columns_scope: allColumns ? 'all' : 'view',
      });
      const csv = await exportCsv({
        reportKey,
        startDate: window_.start,
        endDate: window_.end,
        basis: effectiveBasis,
        groupBy: joinOrUndefined(effectiveGroupBy),
        columns: allColumns ? undefined : joinOrUndefined(effectiveColumns),
        sort: effectiveSort?.key,
        sortDir: effectiveSort?.dir,
        filters: effectiveFilters.length
          ? JSON.stringify(effectiveFilters)
          : undefined,
      }).unwrap();
      triggerDownload(
        new Blob([csv], { type: 'text/csv' }),
        `${reportKey}-${window_.start}-${window_.end}.csv`,
      );
    },
    [
      reportKey,
      effectiveBasis,
      effectiveGroupBy,
      effectiveColumns,
      effectiveSort,
      effectiveFilters,
      window_,
      exportCsv,
      reports,
      triggerDownload,
    ],
  );

  // Split exports only make sense per entity (clinician/patient/payer) —
  // never per category (telehealth). Stored or inherited split choices are
  // validated against the definition's splittable dimensions.
  const splittableKeys = useMemo(
    () =>
      new Set(
        (definition?.dimensions ?? [])
          .filter(dimension => dimension.splittable)
          .map(dimension => dimension.key),
      ),
    [definition],
  );
  const sanitizeSplit = useCallback(
    (candidate: string | undefined | null): string =>
      candidate && splittableKeys.has(candidate) ? candidate : '',
    [splittableKeys],
  );

  // Excel options persist per report; tabs default to the primary grouping.
  const xlsxPrefs = useMemo<XlsxPrefs>(() => {
    const stored = readJson(`${XLSX_PREFS_PREFIX}${reportKey}`) as XlsxPrefs | null;
    return {
      sheetBy: sanitizeSplit(stored?.sheetBy ?? effectiveGroupBy[0]),
      allColumns: stored?.allColumns ?? true,
    };
  }, [reportKey, effectiveGroupBy, sanitizeSplit]);

  const downloadXlsx = useCallback(
    async (options: XlsxPrefs) => {
      writeJson(`${XLSX_PREFS_PREFIX}${reportKey}`, options);
      reports.exportClicked({
        report_key: reportKey,
        basis: effectiveBasis,
        grouped: effectiveGroupBy.length > 0,
        format: 'xlsx',
        columns_scope: options.allColumns ? 'all' : 'view',
        sheet_by: options.sheetBy || undefined,
      });
      const blob = await exportXlsx({
        reportKey,
        startDate: window_.start,
        endDate: window_.end,
        basis: effectiveBasis,
        groupBy: joinOrUndefined(effectiveGroupBy),
        columns: options.allColumns ? undefined : joinOrUndefined(effectiveColumns),
        sheetBy: options.sheetBy || undefined,
        sort: effectiveSort?.key,
        sortDir: effectiveSort?.dir,
        filters: effectiveFilters.length
          ? JSON.stringify(effectiveFilters)
          : undefined,
      }).unwrap();
      triggerDownload(blob, `${reportKey}-${window_.start}-${window_.end}.xlsx`);
    },
    [
      reportKey,
      effectiveBasis,
      effectiveGroupBy,
      effectiveColumns,
      effectiveSort,
      effectiveFilters,
      window_,
      exportXlsx,
      reports,
      triggerDownload,
    ],
  );

  // PDF options persist per report; pages default to the primary grouping.
  const pdfPrefs = useMemo<PdfPrefs>(() => {
    const stored = readJson(`${PDF_PREFS_PREFIX}${reportKey}`) as PdfPrefs | null;
    return {
      pageBy: sanitizeSplit(stored?.pageBy ?? effectiveGroupBy[0]),
      allColumns: stored?.allColumns ?? false,
    };
  }, [reportKey, effectiveGroupBy, sanitizeSplit]);

  const downloadPdf = useCallback(
    async (options: PdfPrefs) => {
      writeJson(`${PDF_PREFS_PREFIX}${reportKey}`, options);
      reports.exportClicked({
        report_key: reportKey,
        basis: effectiveBasis,
        grouped: effectiveGroupBy.length > 0,
        format: 'pdf',
        columns_scope: options.allColumns ? 'all' : 'view',
        sheet_by: options.pageBy || undefined,
      });
      const blob = await exportPdf({
        reportKey,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        startDate: window_.start,
        endDate: window_.end,
        basis: effectiveBasis,
        groupBy: joinOrUndefined(effectiveGroupBy),
        columns: options.allColumns ? undefined : joinOrUndefined(effectiveColumns),
        sheetBy: options.pageBy || undefined,
        sort: effectiveSort?.key,
        sortDir: effectiveSort?.dir,
        filters: effectiveFilters.length
          ? JSON.stringify(effectiveFilters)
          : undefined,
      }).unwrap();
      triggerDownload(blob, `${reportKey}-${window_.start}-${window_.end}.pdf`);
    },
    [
      reportKey,
      effectiveBasis,
      effectiveGroupBy,
      effectiveColumns,
      effectiveSort,
      effectiveFilters,
      window_,
      exportPdf,
      reports,
      triggerDownload,
    ],
  );

  const windowDays = useMemo(() => {
    const start = new Date(window_.start);
    const end = new Date(window_.end);
    const ms = end.getTime() - start.getTime();
    return Math.max(Math.round(ms / (24 * 60 * 60 * 1000)) + 1, 1);
  }, [window_]);

  const saveView = useCallback(
    async (name: string, windowKind: 'relative_days' | 'absolute') => {
      reports.viewSaveSubmitted({ report_key: reportKey });
      const params: SavedViewParams = {
        basis: effectiveBasis,
        group_by: effectiveGroupBy,
        columns: effectiveColumns,
        sort_key: effectiveSort?.key,
        sort_dir: effectiveSort?.dir,
        filters: effectiveFilters.length ? effectiveFilters : undefined,
        window:
          windowKind === 'relative_days'
            ? { kind: 'relative_days', days: windowDays }
            : { kind: 'absolute', start: window_.start, end: window_.end },
      };
      await createSavedView({
        createSavedReportViewRequest: { report_key: reportKey, name, params },
      }).unwrap();
      setLastSavedSnapshot(
        snapshotOf(
          effectiveBasis,
          effectiveGroupBy,
          effectiveColumns,
          effectiveSort,
          effectiveFilters,
        ),
      );
    },
    [
      reportKey,
      effectiveBasis,
      effectiveGroupBy,
      effectiveColumns,
      effectiveSort,
      effectiveFilters,
      window_,
      windowDays,
      createSavedView,
      reports,
      snapshotOf,
    ],
  );

  return {
    definition,
    definitionsLoading,
    window: window_,
    setWindow: setWindowAndMark,
    basis: effectiveBasis,
    setBasis: setBasisAndPersist,
    groupBy: effectiveGroupBy,
    setGroupBy,
    columns: effectiveColumns,
    setColumns,
    isDefaultColumns,
    sort: effectiveSort,
    setSort,
    filters,
    effectiveFilters,
    setFilters,
    isDirty,
    runResult: runState.data,
    isRunning: runState.isFetching,
    hasRun: !runState.isUninitialized,
    runError: runState.error,
    downloadCsv,
    downloadXlsx,
    xlsxPrefs,
    downloadPdf,
    pdfPrefs,
    isExporting:
      exportState.isFetching ||
      exportXlsxState.isFetching ||
      exportPdfState.isFetching,
    saveView,
    isSavingView: createViewState.isLoading,
    windowDays,
    appliedSavedViewName: savedView?.name,
  };
};
